// primary-care-connect-main/frontend/src/pages/Portal/ClinicAdmin/FamilyDoctors/components/AddFamilyDoctorModal.tsx

import React, { useState } from "react";
import { clinicAdminApi, ApiError } from "../../../../../api/clinicAdmin";

export type AddFamilyDoctorModalMode = "CPSO_SEARCH" | "ADD_NEW_PROVIDER";

export type CpsLookupResult = {
  id: number;
  displayName: string;
  cpscNumber: string;
  email?: string;
  gender?: "Male" | "Female";
};

type Props = {
  isOpen: boolean;
  onClose: () => void;

  /**
   * Called when user clicks "Invite" (search result OR manual add).
   * The page decides side effects.
   */
  onInvite?: (familyDoctor: CpsLookupResult) => void;
};

function asString(v: any): string {
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

function isValidEmail(email: string): boolean {
  // v1: simple check, avoids rejecting valid but uncommon addresses.
  const e = email.trim();
  return e.includes("@") && e.includes(".");
}

function normalizeGender(v: any): "Male" | "Female" | "" {
  const s = asString(v).toLowerCase();
  if (s === "male") return "Male";
  if (s === "female") return "Female";
  return "";
}

function normalizeDoctorDisplayName(input: any): string {
  // Goal:
  // - Ensure exactly one "Dr." prefix
  // - Avoid duplicates like "Dr. Dr. Smith"
  // - Be resilient to "Dr", "DR.", extra spaces, etc.
  let s = asString(input);

  // Remove ANY number of leading "dr", "dr.", "dr :" variants
  // e.g. "Dr. Dr John" -> "John"
  s = s.replace(/^(?:\s*dr\.?\s*)+/i, "").trim();

  if (!s) return "";

  return `Dr. ${s}`;
}

export default function AddFamilyDoctorModal({
  isOpen,
  onClose,
  onInvite,
}: Props) {
  const [cpsSearch, setCpsSearch] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const [mode, setMode] = useState<AddFamilyDoctorModalMode>("CPSO_SEARCH");

  // API search state
  const [results, setResults] = useState<CpsLookupResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Inline "complete info" step when a search result lacks email.
  const [selected, setSelected] = useState<CpsLookupResult | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteGender, setInviteGender] = useState<"Male" | "Female" | "">("");
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Manual add form fields
  const [manualCpso, setManualCpso] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [manualName, setManualName] = useState("");
  const [manualGender, setManualGender] = useState<"Male" | "Female" | "">("");
  const [manualError, setManualError] = useState<string | null>(null);

  function resetAll() {
    setCpsSearch("");
    setHasSearched(false);

    setMode("CPSO_SEARCH");

    setResults([]);
    setSearchLoading(false);
    setSearchError(null);

    setSelected(null);
    setInviteEmail("");
    setInviteName("");
    setInviteGender("");
    setInlineError(null);

    setManualCpso("");
    setManualEmail("");
    setManualName("");
    setManualGender("");
    setManualError(null);
  }

  function handleClose() {
    resetAll();
    onClose();
  }

  function startManualAdd(prefillCpso: string) {
    const cpso = asString(prefillCpso);

    // Clear any search artifacts so manual form is always "clean"
    setSelected(null);
    setInlineError(null);
    setSearchError(null);
    setResults([]);
    setSearchLoading(false);

    setMode("ADD_NEW_PROVIDER");
    setManualCpso(cpso);
    setManualEmail("");
    setManualName("");
    setManualGender("");
    setManualError(null);
  }

  function startInvite(result: CpsLookupResult) {
    setInlineError(null);

    const email = asString(result.email);

    // Always require gender before sending invite (new mandatory field)
    setSelected(result);
    setInviteEmail(email);
    setInviteName(normalizeDoctorDisplayName(result.displayName));
    setInviteGender(normalizeGender(result.gender));
  }

  function submitInlineInvite() {
    if (!selected) return;

    const email = asString(inviteEmail);
    if (!email || !isValidEmail(email)) {
      setInlineError("Please enter a valid email address.");
      return;
    }

    const name = normalizeDoctorDisplayName(inviteName);
    if (!name) {
      setInlineError("Doctor name is required.");
      return;
    }

    if (!inviteGender) {
      setInlineError("Doctor gender is required.");
      return;
    }

    const enriched: CpsLookupResult = {
      ...selected,
      email,
      displayName: name,
      gender: inviteGender,
    };

    if (onInvite) onInvite(enriched);
    handleClose();
  }

  function submitManualInvite() {
    setManualError(null);

    const cpso = asString(manualCpso);
    const email = asString(manualEmail);
    const name = normalizeDoctorDisplayName(manualName);

    if (!cpso) {
      setManualError("CPSO number is required.");
      return;
    }
    if (!email || !isValidEmail(email)) {
      setManualError("Please enter a valid email address.");
      return;
    }
    if (!name) {
      setManualError("Doctor name is required.");
      return;
    }
    if (!manualGender) {
      setManualError("Doctor gender is required.");
      return;
    }

    const payload: CpsLookupResult = {
      // Manual entries have no CPSO lookup ID yet. Use a stable local placeholder.
      id: Date.now(),
      displayName: name,
      cpscNumber: cpso,
      email,
      gender: manualGender,
    };

    if (onInvite) onInvite(payload);
    handleClose();
  }

  async function handleSearch() {
    const cpso = asString(cpsSearch);
    if (!cpso) return;

    setHasSearched(true);
    setSelected(null);
    setInlineError(null);
    setManualError(null);

    setSearchLoading(true);
    setSearchError(null);
    setResults([]);

    try {
      const res = await clinicAdminApi.lookupFamilyDoctorByCpso(cpso);

      const mapped: CpsLookupResult[] = (res?.items || []).map((it: any) => {
        const id =
          Number(it?.familyDoctorId) ||
          // if FamilyDoctorID is null in lookup, still provide stable id for UI
          Date.now() + Math.floor(Math.random() * 1000);

        const displayName = normalizeDoctorDisplayName(asString(it?.fullName) || "—");
        const cpscNumber = asString(it?.cpso) || cpso;
        const email = asString(it?.email) || undefined;

        return { id, displayName, cpscNumber, email };
      });

      setResults(mapped);
    } catch (e: any) {
      if (e instanceof ApiError) {
        setSearchError(e.message || "Lookup failed.");
      } else {
        setSearchError(e?.message || "Lookup failed.");
      }
      setResults([]);
    } finally {
      setSearchLoading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal-card" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2>Add / Invite Family Doctor</h2>
          <button
            className="modal-close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {mode === "CPSO_SEARCH" && (
            <>
              <label className="form-label">Search by CPSO Number</label>

              <div className="cps-search-row">
                <input
                  type="text"
                  value={cpsSearch}
                  onChange={(e) => setCpsSearch(e.target.value)}
                  placeholder="Enter CPSO #"
                />
                <button
                  className="primary-btn"
                  onClick={handleSearch}
                  disabled={!cpsSearch.trim() || searchLoading}
                  title={searchLoading ? "Searching…" : undefined}
                >
                  {searchLoading ? "Searching…" : "Search"}
                </button>
              </div>

              {searchError ? (
                <div className="no-results modal-info-block">
                  <p className="modal-error" style={{ margin: 0 }}>
                    {searchError}
                  </p>
                </div>
              ) : null}

              {selected && (
                <div className="no-results modal-info-block">
                  <p style={{ marginBottom: 10 }}>
                    Please confirm the doctor’s details to send the invitation.
                  </p>

                  {inlineError ? (
                    <div className="muted modal-error">{inlineError}</div>
                  ) : null}

                  <div className="modal-field">
                    <label className="form-label">Email (required)</label>
                    <input
                      type="email"
                      className="modal-input"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="doctor@email.com"
                    />
                  </div>

                  <div className="modal-field">
                    <label className="form-label">Doctor gender (required)</label>
                    <select
                      className="modal-input"
                      value={inviteGender}
                      onChange={(e) =>
                        setInviteGender(normalizeGender(e.target.value))
                      }
                    >
                      <option value="">Select…</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>

                  <div className="modal-field">
                    <label className="form-label">Doctor name (required)</label>
                    <input
                      type="text"
                      className="modal-input"
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      placeholder="First Last"
                    />
                    <div className="muted" style={{ marginTop: 6 }}>
                      We will automatically format and display the name with a “Dr.”
                      prefix.
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button
                      className="link-btn"
                      onClick={() => {
                        setSelected(null);
                        setInlineError(null);
                      }}
                    >
                      Back
                    </button>

                    <button className="primary-btn" onClick={submitInlineInvite}>
                      Send Invite
                    </button>
                  </div>
                </div>
              )}

              {hasSearched && !selected && !searchLoading && results.length > 0 && (
                <div className="search-results">
                  {results.map((r) => (
                    <div key={r.id} className="search-result-card">
                      <div>
                        <strong>{r.displayName}</strong>
                        <div className="muted">CPSO #{r.cpscNumber}</div>
                        <div className="muted">{r.email || "—"}</div>
                      </div>

                      <button className="link-btn" onClick={() => startInvite(r)}>
                        Invite
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {hasSearched && !selected && !searchLoading && results.length === 0 && (
                <div className="no-results">
                  <p>No family doctor found with this CPSO number.</p>
                  <button
                    className="link-btn"
                    onClick={() => startManualAdd(cpsSearch.trim())}
                  >
                    + Add New Family Doctor
                  </button>
                </div>
              )}
            </>
          )}

          {mode === "ADD_NEW_PROVIDER" && (
            <>
              <label className="form-label">Invite a Family Doctor</label>

              {manualError ? (
                <div className="muted modal-error">{manualError}</div>
              ) : null}

              <div className="modal-field">
                <label className="form-label">CPSO # (required)</label>
                <input
                  type="text"
                  className="modal-input"
                  value={manualCpso}
                  onChange={(e) => setManualCpso(e.target.value)}
                  placeholder="Enter CPSO #"
                />
              </div>

              <div className="modal-field">
                <label className="form-label">Email (required)</label>
                <input
                  type="email"
                  className="modal-input"
                  value={manualEmail}
                  onChange={(e) => setManualEmail(e.target.value)}
                  placeholder="doctor@email.com"
                />
              </div>

              <div className="modal-field">
                <label className="form-label">Doctor gender (required)</label>
                <select
                  className="modal-input"
                  value={manualGender}
                  onChange={(e) =>
                    setManualGender(normalizeGender(e.target.value))
                  }
                >
                  <option value="">Select…</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>

              <div className="modal-field">
                <label className="form-label">Doctor name (required)</label>
                <input
                  type="text"
                  className="modal-input"
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  placeholder="First Last"
                />
                <div className="muted" style={{ marginTop: 6 }}>
                  We will automatically format and display the name with a “Dr.”
                  prefix.
                </div>
              </div>

              <div className="modal-actions">
                <button
                  className="link-btn"
                  onClick={() => {
                    setMode("CPSO_SEARCH");
                    setManualError(null);
                  }}
                >
                  Back
                </button>

                <button className="primary-btn" onClick={submitManualInvite}>
                  Send Invite
                </button>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="link-btn" onClick={handleClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
